/**
 * Motion-respecting scroll helpers (#28).
 *
 * `scrollIntoView({behavior: 'smooth'})` does NOT consult
 * prefers-reduced-motion — the browser animates regardless, and a long
 * animated scroll is exactly the motion that setting exists to suppress.
 * Every animated scroll in the app goes through here so the call sites
 * can't drift apart again. (Deliberately no count in this sentence — the
 * number was wrong twice while this module was being reviewed, and the
 * stale count is exactly what let one hand-rolled site hide.)
 */
import { anchorMarginFor, ANCHOR_GAP } from './topBar';

/** True when the user asked for reduced motion. Safe on any environment. */
export const prefersReducedMotion = () => {
  if (typeof window === 'undefined' || !window.matchMedia) return false;
  try {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch (_) {
    return false;
  }
};

/**
 * scrollIntoView that degrades to an instant jump under reduced motion.
 * Same destination either way — only the animation is dropped.
 *
 * Uses 'instant', NOT 'auto': per CSSOM-View 'auto' means "defer to the
 * scrolling box's computed scroll-behavior", so a CSS `scroll-behavior:
 * smooth` on any ancestor would silently animate anyway — defeating the
 * guarantee this helper exists to make. This app already has two such
 * containers. 'instant' is unconditional.
 *
 * @param {Element} el     target; no-op when missing (callers often pass a ref)
 * @param {Object}  opts   scrollIntoView options minus `behavior`
 */
export const scrollIntoViewMotionSafe = (el, opts = {}) => {
  if (!el || typeof el.scrollIntoView !== 'function') return;
  const behavior = prefersReducedMotion() ? 'instant' : 'smooth';
  try {
    el.scrollIntoView({ ...opts, behavior });
  } catch (_) {
    // `behavior` is a WebIDL enum: an engine that predates 'instant'
    // THROWS a TypeError rather than ignoring the member. Current
    // browserslist targets all support it, so this is for the tail —
    // but in the chat autoscroll effects that throw would surface in
    // React's commit phase as a blank view, so it is worth two lines.
    el.scrollIntoView({ ...opts, behavior: 'auto' });
  }
};

/**
 * scrollIntoView that must NOT animate, whatever the CSS says.
 *
 * The deliberate-landing twin of `scrollIntoViewMotionSafe`: a deep link
 * ("open Bruce" → the Animals row) is not motion the operator asked for,
 * it is the page arriving, and an animated arrival is both a second of
 * the row being off screen and cancellable by one flick of a thumb.
 *
 * ⚠️ THE FALLBACK IS NOT "DROP THE BEHAVIOUR" (S3-06.4 review). `behavior`
 * is a WebIDL enum, so an engine older than the `'instant'` member —
 * Safari before 15.4 — THROWS a TypeError rather than ignoring it. The
 * obvious catch, calling `scrollIntoView` again with no `behavior` at
 * all, asks for `'auto'`, and `'auto'` per CSSOM-View means "defer to
 * the scrolling box's computed `scroll-behavior`" — which in this app is
 * Bootstrap reboot's `:root { scroll-behavior: smooth }`. So the one
 * engine that needed the fallback got the ~900ms animation the whole
 * rule exists to avoid. The fallback therefore turns `scroll-behavior`
 * OFF on `<html>` for the duration of the call and puts the author's own
 * value back afterwards, inline style and all.
 *
 * @param {Element} el   target; no-op when missing
 * @param {Object}  opts scrollIntoView options minus `behavior`
 */
export const scrollIntoViewInstant = (el, opts = {}) => {
  if (!el || typeof el.scrollIntoView !== 'function') return;
  try {
    el.scrollIntoView({ ...opts, behavior: 'instant' });
    return;
  } catch (_) { /* pre-15.4 Safari: fall through */ }
  const root = (typeof document !== 'undefined') ? document.documentElement : null;
  // The INLINE value, so a page that had set one itself gets it back
  // rather than being reset to "whatever the stylesheet says".
  const previous = root ? root.style.scrollBehavior : null;
  try {
    if (root) root.style.scrollBehavior = 'auto';
    el.scrollIntoView({ ...opts, behavior: 'auto' });
  } catch (_) {
    try { el.scrollIntoView(opts); } catch (__) { /* nothing more to try */ }
  } finally {
    if (root) root.style.scrollBehavior = previous || '';
  }
};

/**
 * window.scrollTo's motion-respecting twin — the OTHER API that animates
 * scrolling, so it belongs to the same decision.
 *
 * @param {Object} opts  scrollTo options minus `behavior` (e.g. {top: 0})
 */
export const scrollToMotionSafe = (opts = {}) => {
  if (typeof window === 'undefined' || typeof window.scrollTo !== 'function') return;
  const behavior = prefersReducedMotion() ? 'instant' : 'smooth';
  try {
    window.scrollTo({ ...opts, behavior });
  } catch (_) {
    window.scrollTo({ ...opts, behavior: 'auto' });
  }
};

/**
 * A DEEP LINK: `el`'s top lands just below the sticky navbar (which since
 * R5 B1 stays at the top for the whole scroll), never under it. The
 * margin is `utils/topBar.anchorMarginFor` — the care guide's rule — set
 * as `el`'s own scroll-margin-top, then a motion-safe
 * `scrollIntoView({ block: 'start' })`.
 */
export const scrollIntoViewBelowTopBar = (el, gap = ANCHOR_GAP) => {
  if (!el || typeof el.scrollIntoView !== 'function') return;
  try {
    if (el.style) el.style.scrollMarginTop = `${anchorMarginFor(el, gap)}px`;
  } catch (_) { /* a margin we cannot set is the old landing, no worse */ }
  scrollIntoViewMotionSafe(el, { block: 'start' });
};
